import classNames from "clsx";
import type * as React from "react";
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { icon } from "../icon.css.ts";
import { XClose } from "../icons/line.tsx";
import { button, closeIcon } from "./CloseX.css.ts";

export type CloseVariants = keyof typeof button.classNames.variants.variant;

type Size = keyof typeof button.classNames.variants.size;
export type Type = keyof typeof button.classNames.variants.type;

export interface Props extends Pick<
  AriaButtonProps,
  "onPress" | "slot" | "aria-label"
> {
  /** Size of the close button. */
  size?: Size;
  /** Shape of the button: `square` (rounded corners) or `circle`. */
  type?: Type;
  /** Color variant matching the parent component's style. */
  variant?: CloseVariants;
  /** Ref to the underlying button element. */
  ref?: React.Ref<HTMLButtonElement>;
}

const sizeToIconSize: Record<Size, Size> = {
  xs: "xs",
  s: "m",
  m: "m",
  l: "l",
};

/**
 * A dismiss/close button with an X icon. Used in modals, alerts, badges, and other dismissible elements.
 */
export const CloseX: React.FC<Props> = ({
  size = "s",
  type = "square",
  variant = "primary",
  ref,
  ...props
}) => {
  return (
    <AriaButton
      ref={ref}
      {...props}
      className={button({ variant, size, type })}
    >
      <XClose
        className={classNames(
          icon({ size: sizeToIconSize[size] }),
          closeIcon({ variant }),
        )}
      />
    </AriaButton>
  );
};
